// src/pages/UserManagementPage.tsx
import React, { useState, useEffect, useMemo } from 'react';
import { getRoleDisplayName } from '../utils/roleDisplayNames';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  TextField,
  InputAdornment,
  Alert,
  CircularProgress,
  Tooltip,
  Pagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Button,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Edit,
  MoreVert,
  CheckCircle,
  Cancel,
  Email,
  Search,
  CalendarToday,
} from '@mui/icons-material';
import { format } from 'date-fns';
import {
  listUsers,
  activateUser,
  deactivateUser,
  resendInvite,
  User,
  UserApiError,
} from '../services/userApi';
import EditUserDialog from '../components/User/EditUserDialog';
import InviteUserDialog from '../components/User/InviteUserDialog';
import { useAuth } from '../contexts/AuthContext';
import { hasRole, hasReadOnlyAdminAccess } from '../services/authApi';
import { 
  getPendingInvitations, 
  resendInvitation, 
  resetInvitationExpiration,
  Invitation,
  InvitationApiError 
} from '../services/invitationApi';

type StatusFilter = 'ALL' | 'ACTIVE' | 'INACTIVE' | 'PENDING' | 'LOCKED';
type UserTypeFilter = 'ALL' | 'TENANT' | 'FRAPORT' | 'CLIENT';

export function UserManagementPage() {
  const { user } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  
  // Only System Admin can edit/delete users (not Exec Admin)
  const canEditUsers = user && hasRole(user, 'System Admin');
  // @ts-ignore - intentionally unused
  const _canPerformActions = user && !hasReadOnlyAdminAccess(user);
  
  // Filters
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('ALL');
  const [userTypeFilter, setUserTypeFilter] = useState<UserTypeFilter>('ALL');
  const [roleFilter, setRoleFilter] = useState<string>('ALL');
  const [searchQuery, setSearchQuery] = useState<string>('');
  
  // Pagination
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [total, setTotal] = useState(0);
  
  // Menu and dialogs
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [menuUserId, setMenuUserId] = useState<number | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [deactivateDialogOpen, setDeactivateDialogOpen] = useState(false);
  const [deactivatingUser, setDeactivatingUser] = useState<User | null>(null);
  const [actionLoading, setActionLoading] = useState<number | null>(null);
  
  // Tabs
  const [selectedTab, setSelectedTab] = useState(0);
  
  // Invitations state
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [invitationsLoading, setInvitationsLoading] = useState(false);
  const [invitationError, setInvitationError] = useState<string | null>(null);
  const [invitationActionLoading, setInvitationActionLoading] = useState<string | null>(null);
  const [resendDialogOpen, setResendDialogOpen] = useState(false);
  const [resendInvitationId, setResendInvitationId] = useState<string | null>(null);
  const [expirationDialogOpen, setExpirationDialogOpen] = useState(false);
  const [expirationInvitationId, setExpirationInvitationId] = useState<string | null>(null);
  const [newExpirationDate, setNewExpirationDate] = useState<string>('');

  // Identity role names (from handoff document)
  const identityRoleNames = [
    'System Admin',
    'Airport Manager',
    'Fraport User',
    'Client User',
    'Tenant User (365 Tenant Sense)',
  ];

  // Get unique roles from users (collect from roles array)
  const availableRoles = useMemo(() => {
    const roles = new Set<string>();
    // Safety check: ensure users is an array
    if (users && Array.isArray(users)) {
      users.forEach(user => {
        // Collect from roles array (Identity system)
        if (user.roles && user.roles.length > 0) {
          user.roles.forEach(role => roles.add(role));
        }
        // Fallback to role field for backward compatibility
        else if (user.role) {
          roles.add(user.role);
        }
      });
    }
    // Sort by Identity role order, then alphabetically
    const sorted = Array.from(roles).sort((a, b) => {
      const aIndex = identityRoleNames.indexOf(a);
      const bIndex = identityRoleNames.indexOf(b);
      if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
      if (aIndex !== -1) return -1;
      if (bIndex !== -1) return 1;
      return a.localeCompare(b);
    });
    return sorted;
  }, [users]);

  // Load users
  const loadUsers = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const params: {
        page?: number;
        pageSize?: number;
        search?: string;
        isActive?: boolean;
      } = {
        page,
        pageSize,
      };
      
      if (searchQuery.trim()) {
        params.search = searchQuery.trim();
      }
      
      if (statusFilter === 'ACTIVE') {
        params.isActive = true;
      } else if (statusFilter === 'INACTIVE') {
        params.isActive = false;
      }
      // PENDING status would need backend support - for now, we'll filter client-side
      
      const response = await listUsers(params);
      console.log('UserManagementPage: API response:', response);
      
      // Ensure response has expected structure
      if (response && response.users && Array.isArray(response.users)) {
        setUsers(response.users);
        setTotal(response.total || response.users.length);
      } else {
        console.warn('UserManagementPage: Unexpected response format:', response);
        setUsers([]);
        setTotal(0);
        setError('Unexpected response format from server');
      }
    } catch (err) {
      const apiError = err as UserApiError;
      const errorMessage = apiError.message || 'Failed to load users';
      setError(errorMessage);
      console.error('Error loading users:', err);
      // Set empty arrays on error to prevent undefined issues
      setUsers([]);
      setTotal(0);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, [page, statusFilter, roleFilter, userTypeFilter]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (page === 1) {
        loadUsers();
      } else {
        setPage(1); // Reset to first page when search changes
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Filter users client-side for user type and role (since API may not support these filters)
  const filteredUsers = useMemo(() => {
    // Safety check: ensure users is an array
    if (!users || !Array.isArray(users)) {
      return [];
    }
    
    let filtered = users;
    
    if (userTypeFilter !== 'ALL') {
      filtered = filtered.filter(user => user.userType === userTypeFilter);
    }
    
    if (roleFilter !== 'ALL') {
      // Check if user has the role in their roles array
      filtered = filtered.filter(user => {
        if (user.roles && user.roles.length > 0) {
          return user.roles.includes(roleFilter);
        }
        // Fallback to role field for backward compatibility
        return user.role === roleFilter;
      });
    }
    
    // PENDING status - users that exist but haven't registered (no identityUserId or emailConfirmed = false)
    if (statusFilter === 'PENDING') {
      filtered = filtered.filter(user => {
        // User is pending if they don't have identityUserId or email is not confirmed
        return !user.identityUserId || !user.emailConfirmed;
      });
    }
    
    // LOCKED status - users with lockoutEnabled and lockoutEnd in the future
    if (statusFilter === 'LOCKED') {
      filtered = filtered.filter(user => {
        return user.lockoutEnabled && user.lockoutEnd && new Date(user.lockoutEnd) > new Date();
      });
    }
    
    return filtered;
  }, [users, userTypeFilter, roleFilter, statusFilter]);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, userId: number) => {
    setMenuAnchor(event.currentTarget);
    setMenuUserId(userId);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setMenuUserId(null);
  };

  const handleEdit = (user: User) => {
    setEditingUser(user);
    setEditDialogOpen(true);
    handleMenuClose();
  };

  // @ts-ignore - intentionally unused
  const _handleEditSuccess = () => {
    setEditDialogOpen(false);
    setEditingUser(null);
    setSuccessMessage('User updated successfully');
    loadUsers();
  };

  const handleActivate = async (userId: number) => {
    try {
      setActionLoading(userId);
      await activateUser(userId);
      setSuccessMessage('User activated successfully');
      loadUsers();
    } catch (err) {
      const apiError = err as UserApiError;
      setError(apiError.message || 'Failed to activate user');
      console.error('Error activating user:', err);
    } finally {
      setActionLoading(null);
      handleMenuClose();
    }
  };

  const handleDeactivateClick = (user: User) => {
    setDeactivatingUser(user);
    setDeactivateDialogOpen(true);
    handleMenuClose();
  };

  const handleDeactivateConfirm = async () => {
    if (!deactivatingUser) return;
    try {
      setActionLoading(deactivatingUser.userId);
      await deactivateUser(deactivatingUser.userId);
      setDeactivateDialogOpen(false);
      setDeactivatingUser(null);
      setSuccessMessage('User deactivated successfully');
      loadUsers();
    } catch (err) {
      const apiError = err as UserApiError;
      setError(apiError.message || 'Failed to deactivate user');
      console.error('Error deactivating user:', err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleResendInvite = async (userId: number) => {
    try {
      setActionLoading(userId);
      await resendInvite(userId);
      setSuccessMessage('Invitation email sent successfully');
    } catch (err) {
      const apiError = err as UserApiError;
      setError(apiError.message || 'Failed to resend invite');
      console.error('Error resending invite:', err);
    } finally {
      setActionLoading(null);
      handleMenuClose();
    }
  };

  const getUserStatus = (user: User): 'ACTIVE' | 'INACTIVE' | 'PENDING' | 'LOCKED' => {
    // Check if account is locked (Identity system)
    if (user.lockoutEnabled && user.lockoutEnd) {
      const lockoutDate = new Date(user.lockoutEnd);
      if (lockoutDate > new Date()) {
        return 'LOCKED';
      }
    }
    
    // PENDING: User exists but hasn't registered (no identityUserId or email not confirmed)
    if (!user.identityUserId || !user.emailConfirmed) {
      return 'PENDING';
    }
    
    // ACTIVE: User is active and has logged in
    if (user.isActive && user.lastLoginAt) {
      return 'ACTIVE';
    }
    
    // INACTIVE: User is not active
    return 'INACTIVE';
  };

  const getUserTypeColor = (userType: string | null) => {
    switch (userType) {
      case 'TENANT':
        return 'primary';
      case 'FRAPORT':
        return 'secondary';
      case 'CLIENT':
        return 'success';
      default:
        return 'default';
    }
  };

  const getUserTypeLabel = (userType: string | null) => {
    switch (userType) {
      case 'TENANT':
        return 'Tenant';
      case 'FRAPORT':
        return 'Fraport';
      case 'CLIENT':
        return 'Client';
      default:
        return 'N/A';
    }
  };

  // Invitation management functions
  const loadInvitations = async () => {
    try {
      setInvitationsLoading(true);
      setInvitationError(null);
      const response = await getPendingInvitations();
      setInvitations(response.invitations);
    } catch (err) {
      const apiError = err as InvitationApiError;
      setInvitationError(apiError.message || 'Failed to load pending invitations');
      console.error('Error loading invitations:', err);
    } finally {
      setInvitationsLoading(false);
    }
  };

  useEffect(() => {
    if (selectedTab === 1) {
      loadInvitations();
    }
  }, [selectedTab]);

  const handleResendInvitation = async (invitationId: string) => {
    try {
      setInvitationActionLoading(invitationId);
      await resendInvitation(invitationId, { extendExpiration: true });
      setSuccessMessage('Invitation resent successfully');
      await loadInvitations();
      setResendDialogOpen(false);
      setResendInvitationId(null);
    } catch (err) {
      const apiError = err as InvitationApiError;
      setInvitationError(apiError.message || 'Failed to resend invitation');
    } finally {
      setInvitationActionLoading(null);
    }
  };

  const handleResetExpiration = async () => {
    if (!expirationInvitationId || !newExpirationDate) return;
    
    try {
      setInvitationActionLoading(expirationInvitationId);
      await resetInvitationExpiration(expirationInvitationId, newExpirationDate);
      setSuccessMessage('Invitation expiration updated successfully');
      await loadInvitations();
      setExpirationDialogOpen(false);
      setExpirationInvitationId(null);
      setNewExpirationDate('');
    } catch (err) {
      const apiError = err as InvitationApiError;
      setInvitationError(apiError.message || 'Failed to update expiration');
    } finally {
      setInvitationActionLoading(null);
    }
  };

  const formatDate = (dateString: string) => {
    return format(new Date(dateString), 'MMM dd, yyyy');
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4" sx={{ color: 'white' }}>
          User Management
        </Typography>
        {canEditUsers && (
          <Button
            variant="contained"
            color="primary"
            onClick={() => setInviteDialogOpen(true)}
          >
            Invite User
          </Button>
        )}
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {successMessage && (
        <Alert severity="success" onClose={() => setSuccessMessage(null)} sx={{ mb: 2 }}>
          {successMessage}
        </Alert>
      )}

      {invitationError && (
        <Alert severity="error" onClose={() => setInvitationError(null)} sx={{ mb: 2 }}>
          {invitationError}
        </Alert>
      )}

      {/* Tabs */}
      <Box sx={{ mb: 3 }}>
        <Tabs 
          value={selectedTab} 
          onChange={(_, newValue) => setSelectedTab(newValue)}
          sx={{
            '& .MuiTab-root': {
              color: 'white',
              '&.Mui-selected': {
                color: 'white',
              },
            },
            '& .MuiTabs-indicator': {
              backgroundColor: 'white',
            },
          }}
        >
          <Tab label="Users" />
          <Tab label="Pending Invitations" />
        </Tabs>
      </Box>

      {selectedTab === 1 ? (
        // Pending Invitations Tab
        <Box>
          {invitationsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Email</TableCell>
                    <TableCell>Role</TableCell>
                    <TableCell>Expiration Date</TableCell>
                    <TableCell>Days Until Expiration</TableCell>
                    <TableCell>Invited By</TableCell>
                    <TableCell>Project</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {invitations.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                        <Typography variant="body2" color="text.secondary">
                          No pending invitations
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    invitations.map((invitation) => (
                      <TableRow key={invitation.invitationId}>
                        <TableCell>{invitation.email}</TableCell>
                        <TableCell>{getRoleDisplayName(invitation.roleName || '')}</TableCell>
                        <TableCell>{formatDate(invitation.expiresAt)}</TableCell>
                        <TableCell>
                          <Chip
                            label={invitation.isExpired ? 'Expired' : `${invitation.daysUntilExpiration} days`}
                            color={invitation.isExpired ? 'error' : invitation.daysUntilExpiration <= 3 ? 'warning' : 'default'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>{invitation.invitedBy.displayName}</TableCell>
                        <TableCell>{invitation.projectName || 'System-wide'}</TableCell>
                        <TableCell align="right">
                          <Tooltip title="Resend Invitation">
                            <IconButton
                              size="small"
                              onClick={() => {
                                setResendInvitationId(invitation.invitationId);
                                setResendDialogOpen(true);
                              }}
                              disabled={invitationActionLoading === invitation.invitationId}
                            >
                              <Email />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Reset Expiration">
                            <IconButton
                              size="small"
                              onClick={() => {
                                setExpirationInvitationId(invitation.invitationId);
                                const currentDate = new Date(invitation.expiresAt);
                                currentDate.setDate(currentDate.getDate() + 7);
                                setNewExpirationDate(currentDate.toISOString().split('T')[0]);
                                setExpirationDialogOpen(true);
                              }}
                              disabled={invitationActionLoading === invitation.invitationId}
                            >
                              <CalendarToday />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      ) : (
        <Box>
          {/* Filters */}
          <Paper sx={{ p: 2, mb: 3 }}>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
              <TextField
                placeholder="Search by name or email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                size="small"
                sx={{ minWidth: 250, flexGrow: 1 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
              />

              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  label="Status"
                  onChange={(e) => {
                    setStatusFilter(e.target.value as StatusFilter);
                    setPage(1);
                  }}
                >
                  <MenuItem value="ALL">All</MenuItem>
                  <MenuItem value="ACTIVE">Active</MenuItem>
                  <MenuItem value="INACTIVE">Inactive</MenuItem>
                  <MenuItem value="PENDING">Pending</MenuItem>
                  <MenuItem value="LOCKED">Locked</MenuItem>
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>User Type</InputLabel>
                <Select
                  value={userTypeFilter}
                  label="User Type"
                  onChange={(e) => {
                    setUserTypeFilter(e.target.value as UserTypeFilter);
                    setPage(1);
                  }}
                >
                  <MenuItem value="ALL">All</MenuItem>
                  <MenuItem value="TENANT">Tenant</MenuItem>
                  <MenuItem value="FRAPORT">Fraport</MenuItem>
                  <MenuItem value="CLIENT">Client</MenuItem>
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>Role</InputLabel>
                <Select
                  value={roleFilter}
                  label="Role"
                  onChange={(e) => {
                    setRoleFilter(e.target.value);
                    setPage(1);
                  }}
                >
                  <MenuItem value="ALL">All</MenuItem>
                  {/* Show Identity role names first (Sprint J: display label for Tenant User) */}
                  {identityRoleNames.map((role) => (
                    <MenuItem key={role} value={role}>
                      {getRoleDisplayName(role)}
                    </MenuItem>
                  ))}
                  {/* Then show any other roles found in users (for backward compatibility) */}
                  {availableRoles
                    .filter(role => !identityRoleNames.includes(role))
                    .map((role) => (
                      <MenuItem key={role} value={role}>
                        {getRoleDisplayName(role)}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Box>
          </Paper>

          {/* Users Table */}
          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Company</TableCell>
                      <TableCell>User Type</TableCell>
                      <TableCell>Roles</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Last Login</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredUsers.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                          <Typography variant="body2" color="text.secondary">
                            No users found
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredUsers.map((user) => {
                        const status = getUserStatus(user);
                        // @ts-ignore - intentionally unused
                        const _isPending = status === 'PENDING';
                        
                        return (
                          <TableRow key={user.userId}>
                            <TableCell>{user.displayName || 'N/A'}</TableCell>
                            <TableCell>{user.email}</TableCell>
                            <TableCell>{user.company || 'N/A'}</TableCell>
                            <TableCell>
                              <Chip
                                label={getUserTypeLabel(user.userType ?? null)}
                                size="small"
                                color={getUserTypeColor(user.userType ?? null) as any}
                              />
                            </TableCell>
                            <TableCell>
                              {user.roles && user.roles.length > 0 ? (
                                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                  {user.roles.map((role, idx) => (
                                    <Chip
                                      key={idx}
                                      label={getRoleDisplayName(role)}
                                      size="small"
                                      color={role === 'System Admin' ? 'error' : role === 'Airport Manager' ? 'warning' : 'default'}
                                    />
                                  ))}
                                </Box>
                              ) : user.role ? (
                                <Chip
                                  label={getRoleDisplayName(user.role)}
                                  size="small"
                                  color={user.role === 'System Admin' ? 'error' : user.role === 'Airport Manager' ? 'warning' : 'default'}
                                />
                              ) : (
                                'N/A'
                              )}
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                <Chip
                                  icon={status === 'ACTIVE' ? <CheckCircle /> : <Cancel />}
                                  label={status}
                                  size="small"
                                  color={
                                    status === 'ACTIVE' ? 'success' : 
                                    status === 'PENDING' ? 'warning' : 
                                    status === 'LOCKED' ? 'error' : 
                                    'default'
                                  }
                                />
                                {/* Identity status indicators */}
                                {user.emailConfirmed && (
                                  <Chip
                                    label="Email Confirmed"
                                    size="small"
                                    color="success"
                                    variant="outlined"
                                    sx={{ fontSize: '0.7rem', height: '20px' }}
                                  />
                                )}
                                {user.lockoutEnabled && user.lockoutEnd && new Date(user.lockoutEnd) > new Date() && (
                                  <Chip
                                    label="Locked"
                                    size="small"
                                    color="error"
                                    variant="outlined"
                                    sx={{ fontSize: '0.7rem', height: '20px' }}
                                  />
                                )}
                              </Box>
                            </TableCell>
                            <TableCell>
                              {user.lastLoginAt
                                ? format(new Date(user.lastLoginAt), 'MMM dd, yyyy')
                                : 'Never'}
                            </TableCell>
                            <TableCell align="right">
                              <Tooltip title="More actions">
                                <IconButton
                                  size="small"
                                  onClick={(e) => handleMenuOpen(e, user.userId)}
                                  disabled={actionLoading === user.userId}
                                >
                                  <MoreVert />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Pagination */}
              {total > pageSize && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                  <Pagination
                    count={Math.ceil(total / pageSize)}
                    page={page}
                    onChange={(_, newPage) => setPage(newPage)}
                    color="primary"
                  />
                </Box>
              )}
            </Box>
          )}
        </Box>
      )}

      {/* Actions Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
      >
        {menuUserId && (() => {
          const user = users.find(u => u.userId === menuUserId);
          if (!user) return null;
          const status = getUserStatus(user);
          const isPending = status === 'PENDING';
          
          return (
            <React.Fragment>
              {canEditUsers && (
                <MenuItem onClick={() => handleEdit(user)}>
                  <Edit sx={{ mr: 1 }} />
                  Edit User
                </MenuItem>
              )}
              {status === 'ACTIVE' ? (
                <MenuItem onClick={() => handleDeactivateClick(user)}>
                  <Cancel sx={{ mr: 1 }} />
                  Deactivate
                </MenuItem>
              ) : (
                <MenuItem onClick={() => handleActivate(user.userId)} disabled={actionLoading === user.userId}>
                  <CheckCircle sx={{ mr: 1 }} />
                  {actionLoading === user.userId ? 'Activating...' : 'Activate'}
                </MenuItem>
              )}
              {isPending && (
                <MenuItem onClick={() => handleResendInvite(user.userId)} disabled={actionLoading === user.userId}>
                  <Email sx={{ mr: 1 }} />
                  {actionLoading === user.userId ? 'Sending...' : 'Resend Invite'}
                </MenuItem>
              )}
            </React.Fragment>
          );
        })()}
      </Menu>

      {/* Invite User Dialog */}
      <InviteUserDialog
        open={inviteDialogOpen}
        onClose={() => setInviteDialogOpen(false)}
        onSuccess={() => {
          setSuccessMessage('User invitation sent successfully');
          loadUsers();
        }}
      />

      {/* Edit User Dialog */}
      {editingUser && (
        <EditUserDialog
          open={editDialogOpen}
          onClose={() => {
            setEditDialogOpen(false);
            setEditingUser(null);
          }}
          onSuccess={() => {
            setSuccessMessage('User updated successfully');
            loadUsers();
            setEditDialogOpen(false);
            setEditingUser(null);
          }}
          user={editingUser}
        />
      )}

      {/* Deactivate Confirmation Dialog */}
      <Dialog open={deactivateDialogOpen} onClose={() => setDeactivateDialogOpen(false)}>
        <DialogTitle>Deactivate User</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to deactivate <strong>{deactivatingUser?.displayName || deactivatingUser?.email}</strong>?
            They will not be able to access the system until reactivated.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeactivateDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleDeactivateConfirm}
            variant="contained"
            color="error"
            disabled={actionLoading !== null}
          >
            {actionLoading ? 'Deactivating...' : 'Deactivate'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Resend Invitation Dialog */}
      <Dialog open={resendDialogOpen} onClose={() => {
        setResendDialogOpen(false);
        setResendInvitationId(null);
      }}>
        <DialogTitle>Resend Invitation</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to resend this invitation? The expiration date will be extended by 7 days.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setResendDialogOpen(false);
            setResendInvitationId(null);
          }}>
            Cancel
          </Button>
          <Button
            onClick={() => resendInvitationId && handleResendInvitation(resendInvitationId)}
            variant="contained"
            color="primary"
            disabled={invitationActionLoading === resendInvitationId}
          >
            {invitationActionLoading === resendInvitationId ? 'Sending...' : 'Resend'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reset Expiration Dialog */}
      <Dialog open={expirationDialogOpen} onClose={() => {
        setExpirationDialogOpen(false);
        setExpirationInvitationId(null);
        setNewExpirationDate('');
      }}>
        <DialogTitle>Reset Expiration Date</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Enter a new expiration date for this invitation.
          </DialogContentText>
          <TextField
            type="date"
            label="New Expiration Date"
            value={newExpirationDate}
            onChange={(e) => setNewExpirationDate(e.target.value)}
            fullWidth
            InputLabelProps={{
              shrink: true,
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setExpirationDialogOpen(false);
            setExpirationInvitationId(null);
            setNewExpirationDate('');
          }}>
            Cancel
          </Button>
          <Button
            onClick={handleResetExpiration}
            variant="contained"
            color="primary"
            disabled={!newExpirationDate || invitationActionLoading === expirationInvitationId}
          >
            {invitationActionLoading === expirationInvitationId ? 'Updating...' : 'Update'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

